/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@eisinc/Overrides/Components/ProductCarouselProduct";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC, ReactNode } from "react";
import { css } from "styled-components";

// eslint-disable-next-line
import SwiperCore, { A11y, Navigation, Pagination, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y]);

export const carouselProductsContainer = css`
    position: relative;
    padding: 0 0 40px 0;
    .swiper-carousel {
        box-sizing: border-box;
        display: flex;
        justify-content: center;
        padding-left: 25px;
        padding-right: 25px;
        z-index: 0;
        @media screen and (max-width: 410px) {
            padding-left: 20px;
            padding-right: 20px;
        }
    }
    .swiper-wrapper {
        box-sizing: border-box;
        z-index: 0;
    }
    .swiper-navigation {
        position: absolute;
        top: 50%;
        width: 100%;
        transform: translateY(-50%);
        z-index: 3;

        .swiper-button-prev,
        .swiper-button-next {
            color: ${getColor("common.ui1")};
            z-index: 2;
            &:after {
                font-size: 33px;
                font-weight: bold;
            }
            &:hover,
            &:focus {
                cursor: pointer;
            }
        }

        .swiper-button-prev {
            left: -5px;
        }
        .swiper-button-next {
            right: -5px;
        }
    }

    .swiper-slide {
        padding: 2px;
        height: auto;
        min-height: 100%;

        @media screen and (min-width: 480px) {
            min-width: 300px;
        }
        @media screen and (min-width: 320px) and (max-width: 480px) {
            min-width: 245px;
        }
        > div {
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
            border: 2px solid transparent;
            &:hover,
            &:focus {
                border: 2px solid ${getColor("common.ui3")};
                box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
            }
        }
    }
`;

interface Props {
    fields: any;
    products: ProductModel[];
    isProductDetailsPage?: boolean;
    isProductListPage?: boolean;
    isBrandDetailsPage?: boolean;
    id: string;
    brand?: any;
    extendedStyles?: ProductCarouselProductStyles;
    hideAvailability?: boolean;
    showElipsesOn?: string;
    spaceBetween?: number;
    customActionsTemplate?: (product: ProductModel) => ReactNode;
    customActions?: boolean;
    showBadges?: boolean;
    // this is used to change the name of navigation arrows to avoid conflic with other carousels on the same page like getting disabled
    carouselName?: string;
    getProductRecommendationId?: any;
}

const ProductCarouselComponent: FC<Props> = ({
    id,
    fields,
    getProductRecommendationId,
    products,
    isProductDetailsPage,
    isProductListPage,
    isBrandDetailsPage,
    brand,
    extendedStyles,
    hideAvailability,
    showElipsesOn,
    customActions,
    customActionsTemplate,
    spaceBetween,
    showBadges,
    carouselName,
}) => {
    const [swiper, setSwiper] = React.useState(undefined as any);
    React.useEffect(() => {
        // eslint-disable-next-line global-require
        require("swiper/swiper-bundle.min.css");
    }, []);
    const [productsLength, setProductLength] = React.useState((products?.length || 0) as number);
    const assignSwiper = (swiper: any) => {
        setSwiper(swiper);

        if (swiper && typeof document !== "undefined") {
            const carouselParent: any = document.querySelectorAll(".carousel-products-container");
            if (carouselParent.length) {
                carouselParent.forEach((carousel: any) => {
                    if (carousel.offsetWidth <= 600) {
                        swiper.params.slidesPerView = 1;
                    }
                    if (carousel.offsetWidth > 600 && carousel.offsetWidth <= 700) {
                        swiper.params.slidesPerView = 2;
                    }
                    if (carousel.offsetWidth > 700 && carousel.offsetWidth <= 1100) {
                        swiper.params.slidesPerView = 3;
                    }
                    if (carousel.offsetWidth > 1100) {
                        swiper.params.slidesPerView = 4;
                    }
                });
            }
        }
    };

    React.useEffect(() => {
        setProductLength(products.length);
    }, [products]);

    const showBrandBlocks = fields.showBrandName && products.some(o => !!o.brand);
    const twoByThreeWrapperStyle = css`
        padding-left: 10px;
        padding-right: 10px;
        > div {
            position: relative;
            margin-top: 20px;
            margin-bottom: 20px;
            margin-left: -50px;
            margin-right: -50px;

            @media screen and (max-width: 1440px) {
                margin-left: 0;
                margin-right: 0;
            }
        }
    `;
    const oneBySixWrapperStyle = css`
        margin-bottom: 20px;
        padding-left: 10px;
        padding-right: 10px;
    `;

    const titleHasParam = fields.title && fields.title.indexOf("{0}") > -1;
    const preparedTitle = titleHasParam && isBrandDetailsPage && brand && fields.title.replace("{0}", brand.name);

    return (
        <StyledWrapper
            css={css`
                padding-top: 15px;
                padding-bottom: 15px;
                > div {
                    display: flex;
                    justify-content: center;
                }
                @media print {
                    display: none;
                }
            `}
        >
            {fields.title && (
                <Typography
                    variant="h2"
                    css={css`
                        font-weight: normal;
                        padding-bottom: 16px;
                        border-bottom-width: ${fields.carouselTemplat === "2*3" ? "3px" : "0"};
                        border-bottom-style: solid;
                        border-bottom-color: ${getColor("common.ui2")};
                        letter-spacing: 0.3px;
                        text-transform: ${fields.uppercaseTitle ? "uppercase" : "initial"};
                    `}
                >
                    {preparedTitle || fields.title}
                </Typography>
            )}

            {fields.carouselTemplat === "1*6" && (
                <GridContainer gap={20} className="one-by-six-container" css={oneBySixWrapperStyle}>
                    {products.slice(0, 6).map((product: ProductModel, index: number) => (
                        <GridItem
                            width={[6, 4, 3, 2, 2]}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`oneBySix${id}${index}`}
                            css={css`
                                background-color: ${getColor("common.white")};
                            `}
                        >
                            <ProductCarouselProduct
                                carouselId={id}
                                product={product}
                                recommendationId={getProductRecommendationId(product)}
                                showImage={fields.showImage}
                                showBrand={showBrandBlocks}
                                showTitle={fields.showTitle}
                                showPartNumbers={fields.showPartNumbers}
                                showPrice={fields.showPrice}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList}
                                view={fields.carouselTemplat}
                                showImageBadges={fields.showImageBadges}
                                maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                showTextBadges={fields.showTextBadges}
                                maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                extendedStyles={extendedStyles}
                                hideAvailability={hideAvailability}
                                showElipsesOn={showElipsesOn}
                            />
                        </GridItem>
                    ))}
                </GridContainer>
            )}
            {fields.carouselTemplat === "2*3" && (
                <GridContainer gap={0} className="two-by-three-container" css={twoByThreeWrapperStyle}>
                    {products.slice(0, 6).map((product: ProductModel, index: number) => (
                        <GridItem
                            // eslint-disable-next-line react/no-array-index-key
                            key={`twoByThree${id}${index}`}
                            css={css`
                                width: calc(100% / 3);
                                flex-basis: calc(100% / 3);
                                max-width: calc(100% / 3);
                                padding-left: 50px;
                                padding-right: 50px;
                                background-color: ${getColor("common.white")};
                                position: relative;
                                border-right-width: ${index === 0 || index === 1 || index === 3 || index === 4
                                    ? "1px"
                                    : "0px"};
                                border-right-style: solid;
                                border-right-color: ${getColor("common.ui2")};
                                padding-top: ${index === 3 || index === 4 || index === 5 ? "18px" : "0px"};
                                padding-bottom: ${index === 0 || index === 1 || index === 2 ? "18px" : "0px"};
                                &:before {
                                    content: " ";
                                    height: ${index === 3 || index === 4 || index === 5 ? "1px" : "0"};
                                    background-color: ${getColor("common.ui2")};
                                    width: 100%;
                                    position: absolute;
                                    top: 0;
                                    right: 0;
                                }

                                @media screen and (max-width: 1440px) {
                                    padding-left: 0;
                                    padding-right: 0;
                                }

                                @media screen and (max-width: 1200px) {
                                    width: calc(100% / 2);
                                    flex-basis: calc(100% / 2);
                                    max-width: calc(100% / 2);
                                    border-right-width: ${index === 0 || index === 2 || index === 4 ? "1px" : "0"};
                                    &:before {
                                        height: ${index === 2 || index === 3 || index === 4 || index === 5
                                            ? "1px"
                                            : "0"};
                                        width: 100%;
                                        right: 0;
                                    }
                                }

                                @media screen and (max-width: 770px) {
                                    width: 100%;
                                    flex-basis: 100%;
                                    max-width: 100%;
                                    border-right-width: 0;
                                    &:before {
                                        height: ${index === 1 ||
                                        index === 2 ||
                                        index === 3 ||
                                        index === 4 ||
                                        index === 5
                                            ? "1px"
                                            : "0"};
                                    }
                                }
                            `}
                        >
                            <ProductCarouselProduct
                                carouselId={id}
                                product={product}
                                showImage={fields.showImage}
                                showBrand={showBrandBlocks}
                                showTitle={fields.showTitle}
                                showPartNumbers={fields.showPartNumbers}
                                showPrice={fields.showPrice}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList}
                                view={fields.carouselTemplat}
                                extendedStyles={extendedStyles}
                                hideAvailability={hideAvailability}
                                showElipsesOn={showElipsesOn}
                            />
                        </GridItem>
                    ))}
                </GridContainer>
            )}
            {fields.carouselTemplat === "carousel" && productsLength > 0 && (
                <StyledWrapper className="carousel-products-container" css={carouselProductsContainer}>
                    <Swiper
                        onInit={swiper => {
                            window.addEventListener("load", function () {
                                setTimeout(() => {
                                    window.dispatchEvent(new Event("resize"));
                                }, 1000);
                            });
                            swiper.update();
                        }}
                        onObserverUpdate={swiper => swiper.update()}
                        spaceBetween={spaceBetween || 40}
                        navigation={{
                            nextEl: `.swiper-button-next.swiper-button-next_${carouselName}_${id}`,
                            prevEl: `.swiper-button-prev.swiper-button-prev_${carouselName}_${id}`,
                        }}
                        onSwiper={swiper => assignSwiper(swiper)}
                        onResize={swiper => swiper.update()}
                        className="swiper-carousel"
                        noSwipingClass="no-swiping-elements"
                        breakpoints={{
                            // when window width is >= 640px
                            0: {
                                slidesPerView: 1,
                            },
                            // when window width is >= 768px
                            700: {
                                slidesPerView: fields.maxNumberOfColumns < 2 ? fields.maxNumberOfColumns : 2,
                            },
                            // when window width is >= 768px
                            1100: {
                                slidesPerView: fields.maxNumberOfColumns < 3 ? fields.maxNumberOfColumns : 3,
                            },
                            // when window width is >= 768px
                            1439: {
                                slidesPerView: fields.maxNumberOfColumns || 4,
                            },
                        }}
                    >
                        {products.map((product: ProductModel, index: any) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <SwiperSlide key={`carouselProduct${id}${index}`}>
                                <ProductCarouselProduct
                                    carouselId={id}
                                    product={product}
                                    showImage={fields.showImage}
                                    showBrand={showBrandBlocks}
                                    showTitle={fields.showTitle}
                                    showPartNumbers={fields.showPartNumbers}
                                    showPrice={fields.showPrice}
                                    showAddToCart={fields.showAddToCart}
                                    showAddToList={fields.showAddToList}
                                    view={fields.carouselTemplat}
                                    extendedStyles={extendedStyles}
                                    hideAvailability={hideAvailability}
                                    showElipsesOn={showElipsesOn}
                                    customActions={customActions}
                                    customActionsTemplate={customActionsTemplate}
                                    showBadges={showBadges}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <StyledWrapper className="swiper-navigation">
                        <div className={`swiper-button-prev swiper-button-prev_${carouselName}_${id}`}></div>
                        <div className={`swiper-button-next swiper-button-next_${carouselName}_${id}`}></div>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default ProductCarouselComponent;
